<script setup lang="ts">
import { Heading } from '~ds/components/ui/typography';
import KUIUnifiedSkillItem from '~kui/components/KUI/Unified/Wallet/Skill/KUIUnifiedSkillItem.vue';
import { useMotionPresets } from '~kui/composables/useMotionPresets';
import { motion } from 'motion-v';
import { useI18n } from 'vue-i18n';

export interface CompletionSkill {
  id: string;
  name: string;
  iconUrl?: string | null;
}

interface Props {
  skills: CompletionSkill[];
  compact?: boolean;
}

withDefaults(defineProps<Props>(), {
  compact: false,
});

const { t } = useI18n();
const { SLIDE_UP, TRANSITION } = useMotionPresets();

const POP_IN = 0.35;
const SHINE = 0.5;
const STAGGER = POP_IN + SHINE;
const BASE_DELAY = 0.3;
</script>

<template>
  <div class="flex flex-col items-center gap-4">
    <motion.div
      v-bind="SLIDE_UP"
      :transition="TRANSITION"
    >
      <Heading
        size="xl"
        as="h2"
        class="mb-8 leading-none"
      >
        {{ t('unified.activity.completion.skillsDisplayed') }}
      </Heading>
    </motion.div>

    <div
      class="flex flex-wrap items-center justify-center"
      :class="compact ? 'gap-4 md:gap-8' : 'gap-8 md:gap-12'"
    >
      <motion.div
        v-for="(skill, index) in skills"
        :key="skill.id"
        class="skill-reveal relative overflow-hidden rounded-full"
        :initial="{ opacity: 0, scale: 0.3 }"
        :animate="{ opacity: 1, scale: 1 }"
        :transition="{ type: 'spring', stiffness: 400, damping: 12, delay: BASE_DELAY + index * STAGGER }"
        :style="{ '--shine-delay': `${BASE_DELAY + index * STAGGER + POP_IN}s` }"
      >
        <KUIUnifiedSkillItem
          :size="compact ? 'md' : 'lg'"
          :name="skill.name"
          :icon-url="skill.iconUrl"
          :earned="true"
        />
      </motion.div>
    </div>
  </div>
</template>

<style scoped>
.skill-reveal::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.6) 50%, transparent 60%);
  transform: translateX(-100%);
  animation: skill-shine 0.5s ease-out var(--shine-delay) forwards;
  pointer-events: none;
}

@keyframes skill-shine {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
</style>
